import * as signalR from "@microsoft/signalr";
import marketdata from "./marketdata.jsx";
import { settings, parsedBrokerUrls } from "./settings.jsx";
import * as utilities from "./utilites.jsx";
import * as authToken from "./authToken.js";
import * as accountMgmt from "./routerAccountMgmt.js";
import * as orderAdjust from "./routerOrderAdjustments.js";
import * as accountData from "./routerAccountData.js";
import * as reports from "./routerReports.js";
import * as orderCommands from "./routerOrderCommands.js";
import * as routerConn from "./routerConnection.js";
import logger from "../../utils/logger.js";
import { playSound } from "../../utils/soundUtils.js";
import { globalToast } from "../../components/ToastProvider.jsx";
import timeoutManager from "../../utils/timeoutManager.js";
import {
  getOrderSideText,
  translateBrokerMessage,
  translateRejectionReason,
} from "../../utils/orderStyles.js";
import {
  isTradingDisabled,
  getTradingDisabledReason,
} from "../../utils/mobileDetection.js";
import { urlManager } from "../../utils/urlManager.js";
import { PRODUCT_NAME, PRODUCT_VERSION } from "../../constants/product.js";
import performanceMonitor from "../../utils/performanceMonitor.js";

class Router {
  constructor() {
    this.signalRToken = "";
    this.broker = "";
    this.user = "";
    this.pwd = "";
    this.receivers = [];
    this.signalRConnection = {};
    this.accountData = {};
    this.lastHeartbeat = 0;
    this.state = "offline";
    this.idle = true;
    this.refreshTokenIntervalId = 0;
    // Track pending order modifications to detect successful updates
    this.recentModifications = []; // Simple array to track recent modifications for better logging
    // Advanced reconnection properties (matching marketdata.jsx patterns)
    this.reconnectionAttempts = 0;
    this.maxReconnectionAttempts =
      parseInt(import.meta.env.VITE_ROUTER_MAX_RECONNECTION_ATTEMPTS) || 10;
    this.baseReconnectionDelay =
      parseInt(import.meta.env.VITE_ROUTER_BASE_RECONNECTION_DELAY) || 10000; // 10 seconds
    this.maxReconnectionDelay =
      parseInt(import.meta.env.VITE_ROUTER_MAX_RECONNECTION_DELAY) || 300000; // 5 minutes
    this.closedByServer = false;
    this.manualLogout = false; // Track if logout was intentional

    // Single-slot cleanup callback (set by orderStore at module load via
    // onRouterDataCleared). Replaces the old direct import of useOrderStore —
    // this file must NOT import orderStore (import cycle). Single slot (last
    // registration wins) keeps HMR re-registration from stacking duplicates.
    this._onRouterDataCleared = null;

    // Throttle state for account data fetches after reports
    this.lastAccountDataFetchTime = 0;
    this.pendingAccountDataFetch = false;
    this.accountDataFetchTimeout = null;
    this.ACCOUNT_DATA_THROTTLE_MS = 2000; // Minimum 2s between fetches after reports

    // Throttle state for account data notifications to receivers
    this.lastAccountDataNotifyTime = 0;
    this.pendingAccountDataNotify = false;
    this.pendingAccountData = null;
    this.accountDataNotifyTimeout = null;
    this.ACCOUNT_DATA_NOTIFY_THROTTLE_MS = 500; // Minimum 500ms between notifications

    // Flag to force immediate notification after order placement
    this._forceImmediateAccountData = false;

    // Track registered position symbols for quote subscriptions (delta-based updates)
    this._registeredPositionSymbols = new Set();

    // Diagnostic counters for issue #317 — read+reset by ordersSnapshot every 120s.
    // Aggregate volume here instead of per-event logger calls to stay under the
    // 20k/hour shipping cap. See project_issue_317_diagnostic.md.
    this._diag = {
      accountDataReceived: 0,
      dedupHits: 0,
      dedupMisses: 0,
      throttleImmediate: 0,
      throttleScheduled: 0,
      throttleAccumulated: 0,
      throttleTimeoutFired: 0,
      throttleMaxActualDelayMs: 0,
      throttleSlowTimeoutCount: 0,
    };
  }

  getUrl = () => {
    const urls = parsedBrokerUrls[marketdata.broker] || [];
    if (urls.length === 0) {
      // This should not happen if runtime-config is properly loaded
      console.error(
        "[OrdersRouter] parsedBrokerUrls is empty - config may not be loaded yet!",
      );
      // Fallback to original settings (but this may use production URLs)
      return settings.brokerUrl[marketdata.broker];
    }
    return urlManager.getCurrentUrl(marketdata.broker, "broker", urls);
  };

  getVersion = () => {
    return settings.routerVersion;
  };

  registerReceiver = (receiver) => {
    if (receiver && typeof receiver === "object") {
      this.receivers.push(receiver);
    }
  };

  unregisterReceiver = (receiver) => {
    this.receivers = this.receivers.filter((r) => r !== receiver);
  };

  // Simplified event handler cleanup
  // ── Connection runbook (delegado p/ routerConnection.js — 🔴) ─────────────
  // Event handlers, login com failover, stop/clear (callback single-slot),
  // política de reconexão e refreshToken. Contratos: routerClearSeam +
  // ordersRouterSessionTransportP1. Estado segue na instância.
  unregisterAllEventHandlers = () =>
    routerConn.unregisterAllEventHandlers(this);
  registerEventHandlers = () => routerConn.registerEventHandlers(this);
  login = (broker, user, pwd) => routerConn.login(this, broker, user, pwd);
  stop = () => routerConn.stop(this);
  onRouterDataCleared = (fn) => routerConn.onRouterDataCleared(this, fn);
  clearRouterData = () => routerConn.clearRouterData(this);
  getReconnectionDelay = () => routerConn.getReconnectionDelay(this);
  resetReconnectionAttempts = () => routerConn.resetReconnectionAttempts(this);
  _shouldAttemptReconnectionOnClose = (error) =>
    routerConn._shouldAttemptReconnectionOnClose(this, error);
  isRecoverableError = (error) => routerConn.isRecoverableError(this, error);
  checkConnectivity = () => routerConn.checkConnectivity(this);
  refreshToken = () => routerConn.refreshToken(this);

  // ── JWT helpers (delegado) ────────────────────────────────────────────────
  // Puras, unificadas em authToken.js (a cópia daqui e a do outro serviço
  // eram idênticas — equivalência pinada em authTokenContract.test.js).
  parseTokenExpiration = (token) => authToken.parseTokenExpiration(token);
  isTokenExpired = (token, bufferMinutes = 5) =>
    authToken.isTokenExpired(token, bufferMinutes);

  getValidToken = () => routerConn.getValidToken(this);
  runConnection = () => routerConn.runConnection(this);

  // ── Account data (delegado p/ routerAccountData.js) ───────────────────────
  // Void-and-push, throttles de fetch/notify e delta-registration de posições.
  // Contrato: routerAccountDataContract.test.js. Estado segue na instância.
  getAccountDataAsync = () => accountData.getAccountDataAsync(this);
  scheduleAccountDataFetch = () => accountData.scheduleAccountDataFetch(this);
  receiveAccountData = (dataString) =>
    accountData.receiveAccountData(this, dataString);
  clearAccountData = () => accountData.clearAccountData(this);
  _notifyAccountDataReceivers = (data) =>
    accountData._notifyAccountDataReceivers(this, data);
  fireUpdateAccountData = (data, immediate = false) =>
    accountData.fireUpdateAccountData(this, data, immediate);

  // ── Report processing (delegado p/ routerReports.js — order-domain 🔴) ────
  // Classificadores de OrdStatus + pipeline do receiveReport (enriquecimento,
  // logs/toasts/sons, fan-out p/ receivers). Contratos: orderReportSeam +
  // orderStoreReceiver + proofs. Estado segue na instância.
  isOpenOrder = (report) => reports.isOpenOrder(this, report);
  isFilledOrder = (report) => reports.isFilledOrder(this, report);
  isCancelledOrder = (report) => reports.isCancelledOrder(this, report);
  isFinalStatusOrder = (report) => reports.isFinalStatusOrder(this, report);
  receiveReport = (dataString) => reports.receiveReport(this, dataString);

  receiveInfo = (dataString) => routerConn.receiveInfo(this, dataString);
  receiveHeartbeat = (time) => routerConn.receiveHeartbeat(this, time);
  isConnectionOK = () => routerConn.isConnectionOK(this);
  inTradeState = () => routerConn.inTradeState(this);

  // ── Ajustes de ordem (delegado p/ routerOrderAdjustments.js) ──────────────
  // Lote padrão, tick mínimo (cache 1-símbolo) e resolução FUT/FUL — cancel
  // não-gated vs entry gated no chokepoint I1. Contrato:
  // routerOrderAdjustmentsContract.test.js.
  adjustFutSymbol = (order) => orderAdjust.adjustFutSymbol(this, order);
  gateAndResolveFuturesOrder = (order) =>
    orderAdjust.gateAndResolveFuturesOrder(this, order);
  translateFutSymbol = (symbol) => orderAdjust.translateFutSymbol(this, symbol);
  checkStandardLot = (symbol, qtt) =>
    orderAdjust.checkStandardLot(this, symbol, qtt);
  adjustStandardLot = (symbol, qtt) =>
    orderAdjust.adjustStandardLot(this, symbol, qtt);
  adjustMinPriceTick = (symbol, px) =>
    orderAdjust.adjustMinPriceTick(this, symbol, px);
  adjustOrderPrices = (order) => orderAdjust.adjustOrderPrices(this, order);

  // ── Comandos de ordem (delegado p/ routerOrderCommands.js — 🔴🔴) ─────────
  // O wire de trading: place/cancel/replace/cancelAll/releaseAll/invert.
  // Contrato: routerOrderCommandsContract.test.js. Estado segue na instância.
  placeOrder = (order) => orderCommands.placeOrder(this, order);
  cancelOrder = (order) => orderCommands.cancelOrder(this, order);
  lookupPositionRemark = (symbol) =>
    orderCommands.lookupPositionRemark(this, symbol);
  cancelAllOrders = () => orderCommands.cancelAllOrders(this);
  cancelAllOrdersForSymbol = (symbol) =>
    orderCommands.cancelAllOrdersForSymbol(this, symbol);
  releaseAllPositions = () => orderCommands.releaseAllPositions(this);
  releaseAllPositionsForSymbol = (symbol) =>
    orderCommands.releaseAllPositionsForSymbol(this, symbol);
  invertPositionForSymbol = (symbol) =>
    orderCommands.invertPositionForSymbol(this, symbol);
  partialPositionForSymbol = (symbol) =>
    orderCommands.partialPositionForSymbol(this, symbol);
  clone = (obj) => orderCommands.clone(this, obj);
  isEmptyObj = (obj) => orderCommands.isEmptyObj(this, obj);
  normalizeNew = (order) => orderCommands.normalizeNew(this, order);
  isObject = (obj) => orderCommands.isObject(this, obj);
  replaceOrder = (order) => orderCommands.replaceOrder(this, order);
  tradability = (symbol) => orderCommands.tradability(this, symbol);

  // ── Account-management HTTP (delegado) ────────────────────────────────────
  // Rotas de segurança/conta extraídas para routerAccountMgmt.js
  // (modularização god-object). Assinaturas/retornos idênticos; contrato
  // pinado em __tests__/routerAccountMgmtContract.test.js.
  forgotPwd = (broker, userid, cpf) =>
    accountMgmt.forgotPwd(this, broker, userid, cpf);
  changePwd = (broker, userid, currentPwd, newPwd) =>
    accountMgmt.changePwd(this, broker, userid, currentPwd, newPwd);
  uploadLog = (broker, userid, _log) =>
    accountMgmt.uploadLog(this, broker, userid, _log);
  isRouterEnabled = (userid) => accountMgmt.isRouterEnabled(this, userid);
  grantRouterAccess = (broker, request) =>
    accountMgmt.grantRouterAccess(this, broker, request);
}

export const router = new Router();

// Expose router for E2E tests (Playwright cannot import ES modules directly)
if (import.meta.env.MODE !== "production") {
  window.__odin_router = router;
}
